// ------------------
// ---- Requests ----
// ------------------

import { getClientUUID } from "./Auth"
import { post, ApiDataResponse } from "./Request"
import { getIntervalString, Interval } from "../navigation/tabs/Tabs"
import {subDays} from "date-fns"

/***
 * Get media ranking with medias/access count list
 * @param startDateTimestamp first date timestamp to request data
 * @param endDateTimestamp last date timestamp to request data
 * @param nodePath organization path of request
 * @param mediaspotSerial optional mediaspot serial
 */
export const getHTTPErrors = async(startDateTimestamp: number, endDateTimestamp: number, nodePath: string, mediaspotSerial?: string, mediaspotSerials?: string[]): Promise<ApiDataResponse<HTTPErrorsCountApiResponse>> => {
    const httpErrorsCountResponse = await post<HTTPErrorsCountApiResponse>("gethttperrorscount", {
        startDateTimestamp: startDateTimestamp,
        endDateTimestamp: endDateTimestamp,
        mediaspotSerial: mediaspotSerial,
        nodePath: nodePath,
        clientUUID: getClientUUID(),
        mediaspotSerials: mediaspotSerials
    })

    return httpErrorsCountResponse
}

/***
 * Get clients browsers repartition usage for specific location
 * @param startDateTimestamp first date timestamp to request data
 * @param endDateTimestamp last date timestamp to request data
 * @param nodePath organization path of request
 * @param mediaspotSerial optional mediaspot serial to filter result on specific path for a specific mediaspot serial
 */
export const getBrowsersRepartition = async(startDateTimestamp: number, endDateTimestamp: number, nodePath: string, mediaspotSerial?: string): Promise<ApiDataResponse<BrowsersRepartitionApiResponse>> => {
    const httpErrorsCountResponse = await post<BrowsersRepartitionApiResponse>("getbrowsersdistribution", {
        startDateTimestamp: startDateTimestamp,
        endDateTimestamp: endDateTimestamp,
        mediaspotSerial: mediaspotSerial,
        nodePath: nodePath,
        clientUUID: getClientUUID()
    })

    return httpErrorsCountResponse
   
}



/***
 * Get list of most visited website domains with number of visits for each one
 * @param startDateTimestamp first date timestamp to request data
 * @param endDateTimestamp last date timestamp to request data
 * @param nodePath organization path of request
 * @param mediaspotSerial optional mediaspot serial to filter data on a node with a specific mediaspot (if multiple mediaspot have been associated to a node)
 */
export const getTopWebsites = async(startDateTimestamp: number, endDateTimestamp: number, nodePath: string, mediaspotSerial?: string): Promise<ApiDataResponse<TopWebsitesApiResponse>> => {
    const mostVisitedWebsitesResponse = await post<TopWebsitesApiResponse>("getmostvisitedwebsites", {
        startDateTimestamp: startDateTimestamp,
        endDateTimestamp: endDateTimestamp,
        mediaspotSerial: mediaspotSerial,
        nodePath: nodePath,
        clientUUID: getClientUUID()
    })

    return mostVisitedWebsitesResponse
}

/***
 * Get in/out data evolution from date
 * @param startDateTimestamp first date timestamp to request data
 * @param endDateTimestamp last date timestamp to request data
 * @param nodePath organization path of request
 * @param mediaspotSerial optional mediaspot serial to filter data on a node with a specific mediaspot
 */
export const getInOutDataEvolution = async(startDateTimestamp: number, endDateTimestamp: number, interval: Interval, nodePath: string, mediaspotSerial?: string): Promise<ApiDataResponse<InoutDataApiResponse>> => {
    return await post<InoutDataApiResponse>("getinouthotspottraffic", {
        startDateTimestamp: startDateTimestamp,
        endDateTimestamp: endDateTimestamp,
        interval: getIntervalString(interval),
        mediaspotSerial: mediaspotSerial,
        nodePath: nodePath,
        clientUUID: getClientUUID()
    })
} 


/***
 * Get evolution of free memory from date
 * @param startDateTimestamp first date timestamp to request data
 * @param endDateTimestamp last date timestamp to request data
 * @param nodePath organization path of request
 * @param mediaspotSerial mediaspot serial
 * @return ApiDataResponse<FreeMemoryEvolutionApiResponse>
 */
export const getFreeMemoryEvolution = async(startDateTimestamp: number, endDateTimestamp: number, nodePath: string, mediaspotSerial: string): Promise<ApiDataResponse<FreeMemoryEvolutionApiResponse>> => {
    const today = new Date(Date.now())
    let dates:string[] = []
    dates.push(today.toISOString())
    for(let i = 0; i < 20 ; i++){
        dates.push(subDays(today, i).toISOString())
    }
                
    return await new Promise(resolve => setTimeout(() => resolve(
        {
            error: undefined,
            data: dates.map(date => {
                return {
                    date: date,
                    freeMemory: 1000,
                }
            })
        }
    )))
}


/***
 * Get evolution of temperature
 * @param startDateTimestamp first date timestamp to request data
 * @param endDateTimestamp last date timestamp to request data
 * @param nodePath organization path of request
 * @param mediaspotSerial mediaspot serial
 * @return ApiDataResponse<TemperatureEvolutionApiResponse>
 */
 export const getTemperatureEvolution = async(startDateTimestamp: number, endDateTimestamp: number, interval: Interval, nodePath: string, mediaspotSerial: string): Promise<ApiDataResponse<TemperatureEvolutionApiResponse>> => {
    const temperatureEvolutionResponse = await post<TemperatureEvolutionApiResponse>("getmodemtemperatureevolution", {
        startDateTimestamp: startDateTimestamp,
        endDateTimestamp: endDateTimestamp,
        interval: getIntervalString(interval),
        mediaspotSerial: mediaspotSerial,
        nodePath: nodePath,
        clientUUID: getClientUUID()
    })
    return temperatureEvolutionResponse
} 



/***
 * Get evolution of wifi connection amount
 * @param startDateTimestamp first date timestamp to request data
 * @param endDateTimestamp last date timestamp to request data
 * @param nodePath organization path of request
 * @param mediaspotSerial mediaspot serial
 * @return ApiDataResponse<TemperatureEvolutionApiResponse>
 */
 export const getWifiConnectionsAmountEvolution = async(startDateTimestamp: number, endDateTimestamp: number, interval: Interval, nodePath: string, mediaspotSerial: string): Promise<ApiDataResponse<WifiConnectionsAmountEvolutionApiResponse>> => {
    const wificConnectionsAmountEvolutionResponse = await post<WifiConnectionsAmountEvolutionApiResponse>("getwificonnectionsevolution", {
        startDateTimestamp: startDateTimestamp,
        endDateTimestamp: endDateTimestamp,
        interval: getIntervalString(interval),
        mediaspotSerial: mediaspotSerial,
        nodePath: nodePath,
        clientUUID: getClientUUID()
    })
    return wificConnectionsAmountEvolutionResponse
} 


/***
 * Get evolution of vehicles count
 * @param startDateTimestamp first date timestamp to request data
 * @param endDateTimestamp last date timestamp to request data
 * @param nodePath organization path of request
 * @param mediaspotSerial mediaspot serial
 * @return ApiDataResponse<VehiclesCountEvolutionApiResponse>
 */
 export const getVehiclesCountAmountEvolution = async(startDateTimestamp: number, endDateTimestamp: number, interval: Interval, nodePath: string, mediaspotSerial: string): Promise<ApiDataResponse<VehiclesCountEvolutionApiResponse>> => {
    const vehiclesCountEvolutionResponse = await post<VehiclesCountEvolutionApiResponse>("getvehiclescountevolution", {
        startDateTimestamp: startDateTimestamp,
        endDateTimestamp: endDateTimestamp,
        interval: getIntervalString(interval),
        mediaspotSerial: mediaspotSerial,
        nodePath: nodePath,
        clientUUID: getClientUUID()
    })
    return vehiclesCountEvolutionResponse
} 

/***
 * Get count of nginx request (grouped by status code)
 * @param startDateTimestamp first date timestamp to request data
 * @param endDateTimestamp last date timestamp to request data
 * @param nodePath organization path of request
 * @param mediaspotSerial mediaspot serial
 * @return ApiDataResponse<NginxRequestsCountApiResponse>
 */
 export const getNginxRequestsStatus = async(startDateTimestamp: number, endDateTimestamp: number, interval: Interval, nodePath: string, mediaspotSerial: string): Promise<ApiDataResponse<NginxRequestsCountApiResponse>> => {
    const nginxRequestsCountResponse = await post<NginxRequestsCountApiResponse>("getnginxrequestscount", {
        startDateTimestamp: startDateTimestamp,
        endDateTimestamp: endDateTimestamp,
        interval: getIntervalString(interval),
        mediaspotSerial: mediaspotSerial,
        nodePath: nodePath,
        clientUUID: getClientUUID()
    })
    return nginxRequestsCountResponse
} 

/***
 * Get count of nginx request (grouped by status code)
 * @param startDateTimestamp first date timestamp to request data
 * @param endDateTimestamp last date timestamp to request data
 * @param nodePath organization path of request
 * @param mediaspotSerial mediaspot serial
 * @return ApiDataResponse<NginxRequestsCountApiResponse>
 */
 export const getNginxRequestsStatusEvolution = async(startDateTimestamp: number, endDateTimestamp: number, interval: Interval, nodePath: string, mediaspotSerial: string): Promise<ApiDataResponse<NginxRequestsCountApiEvolutionResponse>> => {
    const nginxRequestsCountResponse = await post<NginxRequestsCountApiEvolutionResponse>("getnginxrequestscountevolution", {
        startDateTimestamp: startDateTimestamp,
        endDateTimestamp: endDateTimestamp,
        interval: getIntervalString(interval),
        mediaspotSerial: mediaspotSerial,
        nodePath: nodePath,
        clientUUID: getClientUUID()
    })
    return nginxRequestsCountResponse
} 


/***
 * Get evolution of number of hits on monitoring index
 * @param startDateTimestamp first date timestamp to request data
 * @param endDateTimestamp last date timestamp to request data
 * @param nodePath organization path of request
 * @param mediaspotSerial mediaspot serial
 * @return ApiDataResponse<NginxRequestsCountApiResponse>
 */
 export const getMonitorHitsEvolution = async(startDateTimestamp: number, endDateTimestamp: number, interval: Interval, nodePath: string, mediaspotSerial: string): Promise<ApiDataResponse<HitsCountApiResponse>> => {
    const hitsCountResponse = await post<HitsCountApiResponse>("getmonitorhitsevolution", {
        startDateTimestamp: startDateTimestamp,
        endDateTimestamp: endDateTimestamp,
        interval: getIntervalString(interval),
        mediaspotSerial: mediaspotSerial,
        nodePath: nodePath,
        clientUUID: getClientUUID()
    })
    return hitsCountResponse
} 

/***
 * Get evolution of number of hits on monitoring index
 * @param startDateTimestamp first date timestamp to request data
 * @param endDateTimestamp last date timestamp to request data
 * @param nodePath organization path of request
 * @param mediaspotSerial mediaspot serial
 * @return ApiDataResponse<NginxRequestsCountApiResponse>
 */
 export const getRutxAuth = async(startDateTimestamp: number, endDateTimestamp: number, nodePath: string, mediaspotSerial?: string): Promise<ApiDataResponse<RutxAuthApiResponse>> => {
    const rutxAuthResponse = await post<RutxAuthApiResponse>("getrutxauth", {
        startDateTimestamp: startDateTimestamp,
        endDateTimestamp: endDateTimestamp,
        mediaspotSerial: mediaspotSerial,
        nodePath: nodePath,
        clientUUID: getClientUUID()
    })
    return rutxAuthResponse
} 


// -------------------
// ---- Types def ----
// -------------------

export type HTTPErrorsCountApiResponse = Array<{
    status: number,
    count: number
}>

export type BrowsersRepartitionApiResponse = Array<{
    browser: string,
    count: number,
    versions: Array<{
        version: string,
        count: number
    }>
}>

export type CPUEvolutionApiResponse = Array<{
    date: string,
    percent: number
}>

export type TopWebsitesApiResponse = Array<{
    website: string,
    count: number
}>

export type InoutDataApiResponse = Array<{
    date: string,
    tx: number,
    rx: number
}>

export type FreeMemoryEvolutionApiResponse = Array<{
    date: string,
    freeMemory: number
}>

export type TemperatureEvolutionApiResponse = Array<{
    date: string,
    temperature: number
}>

export type WifiConnectionsAmountEvolutionApiResponse = Array<{
    date: string,
    count: number
}>

export type VehiclesCountEvolutionApiResponse = Array<{
    date: string,
    bus_count: number
}>

export type NginxRequestsCountApiResponse = Array<{
    status: string,
    count: number
}>

export type NginxRequestsCountApiEvolutionResponse = Array<{
    date: string,
    codes: Array<{
        code: string,
        count: number
    }>
}>

export type HitsCountApiResponse = Array<{
    date: string,
    hits: number
}>

// export type RutxAuthApiResponse = Array<{
//     message: string,
//     count: number
// }>

export type RutxAuthApiResponse = Array<{
    mediaspotSerial: string
    badPasswordConnectionAttempt: number
    notExistingUserConnectionAttempt: number
    passwordAuthSuccess: number
    pubkeyAuthSucceeded: number
}>